import type { IconValues } from '@gat-ship/icons'
import { icons } from '@gat-ship/icons'

export const accessCodes = {
  administration: {
    agency: {
      view: 'A:Agency:View',
      changeContactInfo: 'A:Agency.ContactInfo:Change'
    }
  },
  system: {
    views: {
      globalSettings: 'S:GlobalSettingsView:Access',
      agencySettings: 'S:AgencySettingsView:Access'
    },
    agency: {
      viewAgencyStructure: 'S:AgencyStructure:View'
    },
    support: {
      createSupportSession: 'S:SupportSession:Create',
      createSupportSessionWithTenantAdminPrivileges:
        'S:SupportSession.TenantAdmin:Grant'
    },
    currency: {
      manageCurrencies: 'S:Currencies:Manage'
    },
    stakeholder: {
      accessStakeholders: 'S:Stakeholder:View',
      addStakeholder: 'S:Stakeholder:Add',
      modifyStakeholder: 'S:Stakeholder:Modify',
      modifySystemReferenceNumbers:
        'S:Stakeholder.SystemReferenceNumbers:Modify',
      modifyBusinessNumbers: 'S:Stakeholder.BusinessNumbers:Modify',
      modifyBillingDetails: 'S:Stakeholder.BillingDetails:Modify',
      addContact: 'S:Stakeholder.Contact:Add',
      viewContact: 'S:Stakeholder.Contact:View',
      modifyContact: 'S:Stakeholder.Contact:Modify',
      removeContact: 'S:Stakeholder.Contact:Remove'
    },
    aiPromptLibrary: {
      manage: 'S:AI.PromptLibrary:Manage',
      overrideModelForRequest: 'S:AI.ModelForRequest:Modify'
    }
  }
} as const

export type AgencyAccessCode =
  (typeof accessCodes.administration.agency)[keyof typeof accessCodes.administration.agency]

export type SystemAccessCode =
  | (typeof accessCodes.system.agency)[keyof typeof accessCodes.system.agency]
  | (typeof accessCodes.system.support)[keyof typeof accessCodes.system.support]
  | (typeof accessCodes.system.currency)[keyof typeof accessCodes.system.currency]
  | (typeof accessCodes.system.stakeholder)[keyof typeof accessCodes.system.stakeholder]
  | (typeof accessCodes.system.views)[keyof typeof accessCodes.system.views]
  | (typeof accessCodes.system.aiPromptLibrary)[keyof typeof accessCodes.system.aiPromptLibrary]

export type AccessCode = AgencyAccessCode | SystemAccessCode

export function isAgencyAccessCode(code: string): code is AgencyAccessCode {
  return Object.values(accessCodes.administration.agency).some(
    (code) => code === code
  )
}

export function isAccessCode(code: string): code is SystemAccessCode {
  return Object.values(accessCodes).some((group) =>
    Object.values(group).some((code) => code === code)
  )
}

export function isStringArrayOfAccessCodes(
  codes: string[]
): codes is SystemAccessCode[] {
  return codes.every((code) => isAccessCode(code))
}

export type AccessCodeMetadata = {
  /** The title of the permission item. */
  title: string
  /** The description of the permission item. */
  description?: string
  /** The access codes that are required for the permission item to be enabled. */
  requiresPermissions?: AccessCode[]
  /** An array of words that can be used to search for the permission item. The search is case-insensitive and matches any character/word. */
  searchWords?: string[]
  /** The privilege level of the permission item. */
  privilegeLevel?: 'high'
  /** When this permission is removed, these are the permissions that should be removed. */
  removedWhenPermissionIsRemoved: AccessCode[]
  /** When these permissions are granted, these permissions should be added to the role. */
  addedWhenPermissionsAreGranted: AccessCode[]
  /** When all these permissions are granted, the permission item should be hidden. */
  hiddenWhenPermissionsAreGranted?: AccessCode[]
}

export interface PermissionGroupMetadata {
  description?: string
  permissionItems: AccessCode[]
  icon: IconValues
}
